import {
  makeAmount,
  makePercent,
  makeUsd,
  Percent,
  PERCENT_DECIMALS,
  PRECISE_DECIMALS,
  PRECISION_DECIMALS,
  Usd,
  USD_DECIMALS,
} from "@silo/types";
import { flow, pipe, Option, Array, String, Number } from "effect";
import { identity } from "fp-ts/lib/function";
import { match, P } from "ts-pattern";
import { parseUnits } from "viem";
import { shiftDecimal } from "./bigint";

export const safeStringify = (value: unknown) => {
  try {
    return JSON.stringify(value);
  } catch (error) {
    if (error instanceof Error) {
      return error.message;
    }

    return "unknown error";
  }
};

export const parseErrorMessage = (error: unknown): string =>
  match(error)
    .with(P.instanceOf(Error), (error) => error.message)
    .with(P.string, identity)
    .otherwise(safeStringify);

/**
 * Truncates any decimals that exceed the precision threshold. This prevents
 * ethers underflow errors when the string is parsed.
 */
export const truncateDecimals = (str: string) => {
  const [integer, decimals] = str.split(".");

  if (!decimals) return integer;

  return [integer, decimals.slice(0, PRECISION_DECIMALS)].join(".");
};

/**
 * Join a list of words with "or"-based grouping
 *
 * ```js
 * disjoin(['A', 'B', 'C']) // => 'A, B, or C'
 * ```
 */
export const disjoin = (words: string[]) =>
  // @ts-ignore (`ListFormat` is not recognized)
  new Intl.ListFormat("en-US", { style: "long", type: "disjunction" }).format(
    words
  );

/**
 * Join a list of words with "and"-based grouping
 *
 * ```js
 * conjoin(['A', 'B', 'C']) // => 'A, B, and C'
 * ```
 */
export const conjoin = (words: string[]) =>
  // @ts-ignore (`ListFormat` is not recognized)
  new Intl.ListFormat("en-US", { style: "long", type: "conjunction" }).format(
    words
  );

/**
 * Removes non-numeric characters from a string (i.e. `$`, `,`)
 */
export function stripNonNumeric(value: string): string {
  return value.replace(/[^-\d.]/g, "");
}

/**
 * Trims decimals of a numerical string down to the max decimals.
 *
 * Does not round trimmed decimals. Does not pad decimals to max.
 *
 * ```
 * trimDecimals('10.4389', 2) // => '10.43'
 * ```
 */
export function trimDecimals(value: string, maxDecimals: number): string {
  const [integer, decimals] = value.split(".");

  if (!integer) return "";

  if (!decimals) return integer;

  return [integer, decimals.slice(0, maxDecimals)].join(".");
}

/**
 * If the value of a numeric string is `0`, removes the dash character.
 *
 * ```ts
 * trimNegativeZero('-0.00%') // => '0.00%'
 * trimNegativeZero('-0.01%') // => '-0.01%'
 * trimNegativeZero('-0') // => '0'
 * ```
 */
export function trimNegativeZero(value: string): string {
  if (parseNumber(value) === 0) return value.replace(/-/g, "");

  return value;
}

/**
 * Strips non-numeric characters before running `parseFloat`
 *
 * ```ts
 * parseFloat("$4.25") // => NaN
 * parseNumber("$4.25") // => 4.25
 * ```
 */
export const parseNumber = flow(stripNonNumeric, parseFloat);

export function parseBigInt(value: string, decimals: number): bigint {
  return pipe(
    value,
    stripNonNumeric,
    (str) => trimDecimals(str, PRECISE_DECIMALS),
    (str) => parseUnits(str, PRECISE_DECIMALS),
    (bi) => shiftDecimal(bi, decimals - PRECISE_DECIMALS)
  );
}

export function parsePercent(value: string): Percent {
  return pipe(parseBigInt(value, PERCENT_DECIMALS), makePercent);
}

export function parsePercentagePoints(value: string): Percent {
  return pipe(parseBigInt(value, PERCENT_DECIMALS - 2), makePercent);
}

export function parseUsd(value: string): Usd {
  return pipe(parseBigInt(value, USD_DECIMALS), makeUsd);
}

export const parseAmount = flow(parseBigInt, makeAmount);

/**
 * All the numerical suffixes supported by `Intl.NumberFormat` `"compact"`
 * display. Used for converting formatted values back to raw values.
 */
const NUMERICAL_SUFFIXES = ["k", "m", "b", "t"];

const firstLetter = /[a-zA-Z]/;

/**
 * Parses a compact notation formatted string value to a raw bigint.
 */
export function parseCompactNotation(value: string, decimals: number): bigint {
  const num = parseNumber(value);

  return pipe(
    firstLetter.exec(value),
    Option.fromNullable,
    Option.flatMap(Array.head),
    Option.map(String.toLowerCase),
    Option.flatMap((letter) => {
      return pipe(
        NUMERICAL_SUFFIXES,
        Array.findFirstIndex((suffix) => suffix === letter)
      );
    }),
    Option.map(Number.increment),
    Option.map(Number.multiply(3)),
    Option.map((exponent) => num * 10 ** exponent),
    Option.getOrElse(() => num),
    (_num) => _num.toString(),
    (str) => parseBigInt(str, decimals)
  );
}

export function isAlphanumeric(value: string): boolean {
  return /^[a-zA-Z0-9]+$/.test(value);
}
